/**
 * Trivia hub at `/trivia` (#295) — a crawlable index of the curated, playable
 * trivia boards. Each PUBLISHED board is a section (its theme), and within it a
 * card PER TOPIC (`/trivia/<slug>`) — the page that renders that topic's clues +
 * the "Play this game" launch of the whole board. A secondary CTA sends visitors to
 * `/host` to create their own game.
 *
 * SSR-safe (no `window`/timers at render top-level) so the prerender bakes it into a
 * crawlable `dist/trivia/index.html`; the head (incl. canonical) comes from
 * {@link headModelFor} via {@link useDocumentHead}. The committed board set is empty
 * in the baseline — that is legal: the page renders a friendly "coming soon" body
 * with a link home rather than an empty grid.
 */

import { Link } from 'react-router-dom';
import {
  TRIVIA_BOARDS,
  publishedTriviaBoards,
  triviaPagesForBoards,
  type TriviaBoard,
  type TriviaPage,
} from '@jeopardy/shared';
import { BRAND_NAME } from '../ui/brand';
import { LandingHeader } from '../ui/landing/LandingHeader';
import { useDocumentHead } from '../seo/useDocumentHead';
import { headModelFor } from '../seo/route-seo';

/** A published board plus its topic pages, in board order. */
interface BoardSection {
  board: TriviaBoard;
  pages: TriviaPage[];
}

export function TriviaIndex(): React.JSX.Element {
  useDocumentHead(headModelFor('/trivia'));

  const boards = publishedTriviaBoards(TRIVIA_BOARDS);
  // Group the (globally deduped) page list by board so links never 404 and slugs
  // match routing/sitemap exactly. One section per board, one card per topic.
  const pagesByBoard = new Map<string, TriviaPage[]>();
  for (const page of triviaPagesForBoards(TRIVIA_BOARDS)) {
    const list = pagesByBoard.get(page.boardId) ?? [];
    list.push(page);
    pagesByBoard.set(page.boardId, list);
  }
  const sections: BoardSection[] = boards
    .map((board) => ({ board, pages: pagesByBoard.get(board.boardId) ?? [] }))
    .filter((s) => s.pages.length > 0);

  return (
    <div className="landing" data-testid="trivia-index-page">
      <LandingHeader />

      <header className="landing-wrap landing-hero">
        <div className="landing-hero__copy">
          <span className="landing-eyebrow">Trivia</span>
          <h1 className="landing-hero__title">
            Trivia questions <span className="landing-amber">and answers</span>
          </h1>
          <p className="landing-hero__lead">
            Browse free trivia questions by topic, reveal the answers, then play the whole
            board as a live {BRAND_NAME} game — one shared screen, phones as buzzers, no
            app to install.
          </p>
        </div>
      </header>

      {sections.length === 0 ? (
        <section
          className="landing-wrap landing-section landing-why"
          data-testid="trivia-index-empty"
        >
          <div className="landing-sec-head">
            <h2>Fresh boards are on the way</h2>
            <p>
              We are curating playable trivia boards right now. In the meantime, spin up
              your own game on any topic you like.
            </p>
          </div>
          <div className="landing-cta-row">
            <Link className="btn btn--primary landing-cta" to="/demo">
              Play a fresh game
            </Link>
            <Link className="btn btn--ghost landing-cta" to="/">
              Back to home
            </Link>
          </div>
        </section>
      ) : (
        <>
          {sections.map(({ board, pages }) => (
            <section
              className="landing-wrap landing-section"
              key={board.boardId}
              data-testid="trivia-board-section"
            >
              <div className="landing-sec-head">
                <h2>{board.theme}</h2>
                <p>{board.description}</p>
              </div>
              <div className="trivia-grid">
                {pages.map((page) => (
                  <Link
                    className="trivia-card"
                    key={page.slug}
                    to={`/trivia/${page.slug}`}
                    data-testid="trivia-topic-card"
                  >
                    <h3>{page.topic}</h3>
                    <span className="trivia-card__count">{page.items.length} clues</span>
                  </Link>
                ))}
              </div>
            </section>
          ))}

          <section
            className="landing-wrap landing-section landing-why"
            data-testid="trivia-index-cta"
          >
            <div className="landing-sec-head">
              <h2>Want a board on your own topics?</h2>
              <p>
                Create your own game on any subject — pick a topic and host it in minutes.
              </p>
            </div>
            <div className="landing-cta-row">
              <Link
                className="btn btn--primary landing-cta trivia-cta-full"
                to="/host"
                data-testid="trivia-index-create"
              >
                Create your own game
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
